import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Controller from '@ember/controller';
import { copy } from 'ember-copy';
import { task, timeout } from 'ember-concurrency';
import AttributesProxy from 'cio/app-models/customer/attributes-proxy';
import config from 'cio/config/environment';
import { areObjectsEqual } from 'cio/utils/objects';
import AlertsService from 'cio/services/alerts';
import ApiErrorsService from 'cio/services/api-errors';
import { ApiError, ApiResponse } from 'cio/services/api';
import { CREATED_IN_CIO_AT } from 'cio/utils/attributes';
import { ObjectIdentifierKey } from 'cio/utils/identifiers';
import DS from 'ember-data';
import RouterService from '@ember/routing/router-service';
import ObjectTypeModel from 'cio/models/object-type';
import ObjectModel from 'cio/models/object';

export default class EditController extends Controller {
  @service alerts!: AlertsService;
  @service apiErrors!: ApiErrorsService;
  @service store!: DS.Store;
  @service router!: RouterService;

  interval = config.APP.peopleDelay;
  attributesProxy: AttributesProxy | null = null;
  declare objectType: ObjectTypeModel;
  declare model: ObjectModel;
  isNew = false;

  @computed('attributesProxy.isDirty')
  get isDirty() {
    return this.attributesProxy?.isDirty ?? false;
  }

  prepareAttributesProxy() {
    let attrs = this.model.filteredAttrsWithIdentifiers as Record<string, string>[];

    this.set(
      'attributesProxy',
      AttributesProxy.create({
        attrs,
        requiredAttributes: [ObjectIdentifierKey.ObjectId],
        permanentAttributes: [ObjectIdentifierKey.CioObjectId, ObjectIdentifierKey.ObjectId, CREATED_IN_CIO_AT],
        autoGeneratedAttributes: [ObjectIdentifierKey.CioObjectId, CREATED_IN_CIO_AT],
        isNew: this.isNew,
      }),
    );
  }

  // Since object updates are async, we don't get immediate feedback
  // on whether and when the change actually gets persisted.
  // We issue a GET request after a timeout to check if the object
  // has been updated yet.
  isObjectUpdated = task(async () => {
    let targetAttributes = copy(this.model.attributes);

    this.alerts.sticky('object.update', 'Updating your object…');

    await timeout(this.interval);

    try {
      let apiObject = await this.store.findRecord('object', this.model.id);
      if (areObjectsEqual(targetAttributes, apiObject.attributes)) {
        this.alerts.remove('object.update');
      } else {
        this.alerts.warning('object.update', 'Your object will be updated soon.');
      }

      this.model.rollbackAttributes();
      this.attributesProxy?.reset();
      this.router.transitionTo('object.attributes.index', this.model.id);
    } catch (e) {
      this.alerts.remove('object.update');
      throw e;
    }
  });

  save = task(async (attributes: Record<string, any>, hiddenAttributes: Record<string, any>) => {
    this.model.setProperties({
      attributes,
      hiddenAttributes,
    });

    try {
      await this.model.save();
    } catch (err) {
      this.model.rollbackAttributes();
      return this.processServerError(err);
    }
    this.objectType.updateSentAttributes(attributes);

    await this.isObjectUpdated.perform();
  });

  @action
  reset() {
    this.model.rollbackAttributes();
    this.router.transitionTo('object.attributes.index', this.model.id);
  }

  @action
  processServerError(apiError: ApiResponse) {
    let error: Record<string, any> = (apiError.errors as ApiError[] | undefined)?.[0] ?? {};

    if (error.status === '422' || error.source != null) {
      let attribute = error.source?.pointer?.replace('/data/attributes/', '');

      this.attributesProxy?.findAttrByKey(attribute)?.set('serverError', error.detail);

      return;
    } else if (error.status === '403') {
      this.alerts.error(
        'object.error',
        `You do not have permission to ${this.isNew ? 'create' : 'edit'} this object. Please contact your Admin.`,
      );
      return;
    }

    this.apiErrors.report(apiError);
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your controllers.
declare module '@ember/controller' {
  interface Registry {
    'object.attributes.edit': EditController;
  }
}
